import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Loader2, CheckCircle2, Clock, AlertTriangle, Lock, BadgeCheck, Mail, IdCard, Briefcase } from 'lucide-react';
import BackBar from '../components/BackBar';
import Footer from '../components/Footer';
import PhoneInput from '../components/PhoneInput';
import { fetchProfiles, fetchProfile, updateProfile, uploadProfileAvatar } from '../api/profiles';
import { getMe, fetchMyApplications, getDashboardStatus, getPortalToken, portalErrorMessage } from '../api/portal';
import StatusTab from '../components/dashboard/StatusTab';
import MatchesTab from '../components/dashboard/MatchesTab';
import DocumentsTab from '../components/dashboard/DocumentsTab';
import Avatar from '../components/Avatar';
import './ApplyPage.css';
import './DashboardPage.css';

// Candidate self-service dashboard — "My Profile" (Full name/Phone editable pre-promotion; Passport
// number and Professions applied for are always read-only display fields) plus Status/Matches/
// Documents tabs. Reachable only signed-in (same one-shot token check pattern as ApplyPage.js) —
// anonymous visitors are bounced to /login. Document viewing lives solely in the Documents tab now
// (2026-07-25) — no separate "documents on file" list here, so DOC_LABELS/FileText aren't needed.
// Skills removed entirely (owner ask, 2026-07-25) — not needed.

export default function DashboardPage() {
  const navigate = useNavigate();
  // Defaults to Status (owner ask, 2026-07-25) — landing on the read-only profile-summary tab after
  // login was a dead end; Status is the page's actual "what's happening with my application" content.
  // My Profile stays reachable via the header pill (onClick={() => setTab('profile')}).
  const [tab, setTab] = useState('status');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [account, setAccount] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [unseenMatches, setUnseenMatches] = useState(0);

  // Edit-form state, seeded from the default (or first) profile once loaded. Single "Full name"
  // field (matching the artifact) — split into firstName/lastName on save, same convention as
  // RegisterGate, so the backend contract (which still stores the two columns) is unchanged.
  // nameLocked (2026-07-25): true once staff has promoted this account to a real ERP Candidate —
  // `name` is then the verified ERP name and is no longer editable (PUT /profiles/:id also rejects
  // a name change server-side as a backstop; see profiles.ts's getLinkedCandidateName).
  const [fullName, setFullName] = useState('');
  // The exact value loaded from the server — compared against `fullName` on save so an unrelated
  // edit (phone/skills only) never re-splits and rewrites firstName/lastName (code-review finding,
  // 2026-07-25): re-deriving from a full name that was originally stored with a DIFFERENT split
  // convention (e.g. a pre-rework account, or a 3+-word name split by a human) would silently
  // overwrite a correct firstName/lastName with this component's own first-space guess.
  const [loadedFullName, setLoadedFullName] = useState('');
  const [nameLocked, setNameLocked] = useState(false);
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const avatarInputRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file || !profile) return;
    setAvatarUploading(true); setAvatarError('');
    try {
      await uploadProfileAvatar(profile.id, file);
      const fresh = await fetchProfile(profile.id);
      setProfile(fresh);
    } catch (err) {
      setAvatarError(portalErrorMessage(err, 'Could not upload your photo. Please try again.'));
    } finally {
      setAvatarUploading(false);
    }
  }

  useEffect(() => {
    if (!getPortalToken()) {
      navigate('/login');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [acc, list, apps, status] = await Promise.all([getMe(), fetchProfiles(), fetchMyApplications(), getDashboardStatus()]);
        if (cancelled) return;
        setAccount(acc);
        setProfiles(list);
        setApplications(apps);
        setUnseenMatches(status.unseenMatchCount);
        if (list.length > 0) {
          const def = list.find((p) => p.isDefault) || list[0];
          const full = await fetchProfile(def.id);
          if (cancelled) return;
          setProfile(full);
          setFullName(full.name || '');
          setLoadedFullName(full.name || '');
          setNameLocked(!!full.nameLocked);
          setPhone(full.phone || '');
        }
      } catch (err) {
        if (!cancelled) setLoadError(portalErrorMessage(err, 'Could not load your account. Please try again.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  async function handleSave(e) {
    e?.preventDefault();
    if (!profile || saving) return;
    setSaving(true);
    setSaveError('');
    setSaved(false);
    try {
      const payload = {};
      // Phone joins the name lock once promoted (owner ask, 2026-07-25 — supersedes the earlier
      // "phone stays editable regardless" decision): once verified, the whole profile mirrors the
      // confirmed ERP record rather than staying an editable form.
      if (!nameLocked) payload.phone = phone.trim();
      if (!nameLocked && fullName.trim() !== loadedFullName.trim()) {
        // Only re-split when the Full Name field itself was actually edited — resaving after only
        // touching phone/skills must never re-derive and silently overwrite an already-correct
        // firstName/lastName (e.g. a 3+-word name split differently than this component's own
        // first-space convention would guess). Split on first space, same convention as
        // RegisterGate.splitName — the backend still stores firstName/lastName as two columns even
        // though this is one visible field.
        const trimmed = fullName.trim().replace(/\s+/g, ' ');
        const spaceAt = trimmed.indexOf(' ');
        payload.firstName = spaceAt === -1 ? trimmed : trimmed.slice(0, spaceAt);
        payload.lastName = spaceAt === -1 ? '' : trimmed.slice(spaceAt + 1);
      }
      const updated = await updateProfile(profile.id, payload);
      setProfile((prev) => ({ ...prev, ...updated }));
      setLoadedFullName(fullName.trim());
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(portalErrorMessage(err, 'Could not save your changes. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <BackBar variant="default" showJobsLink />
      <main className="apply-main container">
        <div className="apply-card dash-card">
          {!loading && !loadError && account?.hasApplication ? (
            <div className="dash-greeting-row">
              <h1 className="apply-title dash-greeting-name">
                Hi {account.firstName || 'there'}
                {account.promoted && (
                  <BadgeCheck size={18} className="dash-verified-badge" aria-label="Verified" />
                )}
              </h1>
              <button type="button" className="dash-profile-link" onClick={() => setTab('profile')}>
                <User size={16} /> My Profile
              </button>
            </div>
          ) : (
            <div className="apply-head">
              <h1 className="apply-title">My account</h1>
              <p className="apply-sub">
                {account ? `Signed in as ${account.email}` : 'Manage your profile and track your applications.'}
              </p>
            </div>
          )}

          {!loading && !loadError && account?.hasApplication && (
          <div className="dash-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'status'}
              className={tab === 'status' ? 'dash-tab dash-tab-on' : 'dash-tab'}
              onClick={() => setTab('status')}>
              Status
            </button>
            <button type="button" role="tab" aria-selected={tab === 'matches'}
              className={tab === 'matches' ? 'dash-tab dash-tab-on' : 'dash-tab'}
              onClick={() => { setTab('matches'); setUnseenMatches(0); }}>
              Matches
              {unseenMatches > 0 && <span className="dash-tab-badge">{unseenMatches}</span>}
            </button>
            <button type="button" role="tab" aria-selected={tab === 'documents'}
              className={tab === 'documents' ? 'dash-tab dash-tab-on' : 'dash-tab'}
              onClick={() => setTab('documents')}>
              Documents
            </button>
          </div>
          )}

          {loading && (
            <div className="dash-loading"><Loader2 className="spin" size={22} /></div>
          )}

          {!loading && loadError && <div className="apply-error" role="alert">{loadError}</div>}

          {/* candidate-registration-rework: an account can exist (OTP-verified) before registration
              is actually complete — mandatory CV+passport not yet uploaded, so no Candidate row
              exists yet. Gate every tab behind that, rather than showing empty/broken Status/Matches/
              Documents panels for an account with nothing there to show. */}
          {!loading && !loadError && account && !account.hasApplication && (
            <div className="dash-panel dash-gate">
              <Clock size={32} className="dash-gate-icon" />
              <h2>Finish your registration</h2>
              <p>You verified your email, but your application isn&apos;t complete yet — upload your CV and passport to finish and see your status.</p>
              {/* "Profession selected" can only ever show done once a Candidate row exists
                  (professions aren't persisted before the final application submit — the apply
                  wizard's profession step is frontend-only state until POST /applications) — so on
                  THIS gate it's realistically always "not yet", same moment hasApplication flips
                  true and the gate itself disappears. Kept for artifact-structure fidelity. */}
              <ul className="dash-gate-checklist">
                <li><CheckCircle2 size={16} /> Email verified</li>
                {account.professions?.length > 0 ? (
                  <li><CheckCircle2 size={16} /> Profession selected</li>
                ) : (
                  <li className="dash-gate-todo"><AlertTriangle size={16} /> Profession not selected</li>
                )}
                <li className="dash-gate-todo"><AlertTriangle size={16} /> CV &amp; passport not uploaded</li>
              </ul>
              <Link to="/apply" className="apply-btn apply-btn-link">Continue registration</Link>
            </div>
          )}

          {!loading && !loadError && account?.hasApplication && tab === 'profile' && (
            <div className="dash-panel">
              {profiles.length === 0 ? (
                <div className="dash-empty">
                  <p>No saved profile documents on file yet.</p>
                  <Link to="/" className="apply-link-btn">Browse jobs</Link>
                </div>
              ) : (
                <>
                  <form className="apply-step" onSubmit={handleSave}>
                    <div className="dash-avatar-row">
                      <Avatar profileId={profile.id} avatarPath={profile.avatarPath} name={fullName} size={72} />
                      <div>
                        <button type="button" className="apply-link-btn" style={{ marginTop: 0, padding: 0 }}
                          onClick={() => avatarInputRef.current?.click()} disabled={avatarUploading}>
                          {avatarUploading ? <Loader2 className="spin" size={14} /> : null} Change photo
                        </button>
                        <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleAvatarChange} />
                      </div>
                    </div>
                    {avatarError && <div className="apply-error" role="alert">{avatarError}</div>}
                    {saveError && <div className="apply-error" role="alert">{saveError}</div>}

                    <label className="apply-label" htmlFor="dash-name"><User size={18} /> Full name</label>
                    <input id="dash-name" type="text" autoComplete="name"
                      className="apply-input" placeholder="As on your passport"
                      value={fullName} onChange={(e) => setFullName(e.target.value)}
                      readOnly={nameLocked} disabled={nameLocked} />
                    {nameLocked && (
                      <p className="apply-hint dash-name-locked"><Lock size={12} /> Verified — matches your ERP record and can't be changed here.</p>
                    )}

                    {account.promoted && (
                      <>
                        <label className="apply-label" htmlFor="dash-passport" style={{ marginTop: '1rem' }}>
                          <IdCard size={18} /> Passport number
                        </label>
                        <input id="dash-passport" type="text" className="apply-input"
                          value={account.passportNumber || 'Not yet recorded'} readOnly disabled />
                      </>
                    )}

                    <label className="apply-label" htmlFor="dash-phone" style={{ marginTop: '1rem' }}>
                      Phone number {!nameLocked && <span className="apply-optional">(optional)</span>}
                    </label>
                    <PhoneInput id="dash-phone" value={phone} onChange={setPhone} disabled={nameLocked} />

                    <label className="apply-label" htmlFor="dash-email" style={{ marginTop: '1rem' }}>
                      <Mail size={18} /> Email
                    </label>
                    <input id="dash-email" type="email" className="apply-input" value={account.email} readOnly disabled />

                    <label className="apply-label" style={{ marginTop: '1rem' }}>
                      <Briefcase size={18} /> Professions applied for
                    </label>
                    {account.professions?.length > 0 ? (
                      <div className="dash-tag-input dash-tag-input-static">
                        {account.professions.map((p) => (
                          <span key={p} className="dash-tag dash-tag-static">{p}</span>
                        ))}
                      </div>
                    ) : (
                      <p className="apply-hint" style={{ marginTop: 0 }}>None yet.</p>
                    )}

                    {!nameLocked && (
                      <button type="submit" className="apply-btn" disabled={!fullName.trim() || saving}>
                        {saving ? <Loader2 className="spin" size={18} /> : saved ? <CheckCircle2 size={18} /> : null}
                        {saved ? 'Saved' : 'Save changes'}
                      </button>
                    )}
                  </form>
                </>
              )}
            </div>
          )}

          {!loading && !loadError && account?.hasApplication && tab === 'status' && <StatusTab />}
          {!loading && !loadError && account?.hasApplication && tab === 'matches' && <MatchesTab onVisitOffice={() => setTab('status')} />}
          {!loading && !loadError && account?.hasApplication && tab === 'documents' && <DocumentsTab />}
        </div>
      </main>
      <Footer />
    </>
  );
}
